import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { SessionService } from './session.service';
import { SubscriptionService } from './subscription.service';
import { TranslocoService } from '@jsverse/transloco';
import { AppConfigService } from './appconfig.service';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const WARNING_SECONDS = 60;
const IDLE_NOTIFICATION_ID = 'idle-session-timeout';
const DEFAULT_TIMEOUT_MINUTES = 30;

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  private timeoutMs = DEFAULT_TIMEOUT_MINUTES * 60 * 1000;
  private warningMs = this.timeoutMs - WARNING_SECONDS * 1000;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  private started = false;
  private enabled = false;
  private configSub: Subscription | null = null;

  /** Emits the remaining seconds once warning phase starts; null when not warning */
  private readonly _warning$ = new BehaviorSubject<number | null>(null);
  readonly warning$ = this._warning$.asObservable();

  constructor(
    private ngZone: NgZone,
    private msalService: MsalService,
    private sessionService: SessionService,
    private subscriptionService: SubscriptionService,
    private translocoService: TranslocoService,
    private appConfigService: AppConfigService,
  ) {}

  /** Call once after user is authenticated and the app is ready */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.configSub = combineLatest([
      this.appConfigService.featureFlags$,
      this.appConfigService.sessionTimeoutMinutes$,
    ]).subscribe(([flags, timeoutMinutes]) => {
      const newEnabled = flags.sessionTimeout;
      const newTimeoutMs = timeoutMinutes * 60 * 1000;

      const wasEnabled = this.enabled;
      const timeoutChanged = newTimeoutMs !== this.timeoutMs;

      this.enabled = newEnabled;
      this.timeoutMs = newTimeoutMs;
      this.warningMs = this.timeoutMs - WARNING_SECONDS * 1000;

      if (!newEnabled) {
        this.stopListening();
        return;
      }

      if (!wasEnabled || timeoutChanged) {
        this.startListening();
      }
    });
  }

  private startListening(): void {
    this.stopListening();

    this.ngZone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach(event =>
        document.addEventListener(event, this.onActivity, { passive: true })
      );
    });

    this.resetTimers();
  }

  private stopListening(): void {
    this.clearAllTimers();
    this._warning$.next(null);
    this.removeIdleNotification();

    ACTIVITY_EVENTS.forEach(event =>
      document.removeEventListener(event, this.onActivity)
    );
  }

  /** Call to dismiss the warning and reset (e.g. user clicks "Stay logged in") */
  stayLoggedIn(): void {
    this.resetTimers();
  }

  private onActivity = (): void => {
    // Only reset if we are NOT already in the warning phase
    if (this._warning$.value === null) {
      this.resetTimers();
    }
  };

  private resetTimers(): void {
    this.clearAllTimers();
    this._warning$.next(null);
    this.removeIdleNotification();

    this.warningTimer = setTimeout(() => this.startWarningPhase(), this.warningMs);
    this.idleTimer = setTimeout(() => this.performLogout(), this.timeoutMs);
  }

  private startWarningPhase(): void {
    let remaining = WARNING_SECONDS;
    this._warning$.next(remaining);
    this.pushIdleNotification(remaining);

    this.countdownInterval = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        this.clearCountdown();
      }
      this._warning$.next(Math.max(remaining, 0));
      this.updateIdleNotificationMessage(Math.max(remaining, 0));
    }, 1000);
  }

  private pushIdleNotification(seconds: number): void {
    const notification = {
      id: IDLE_NOTIFICATION_ID,
      type: 'warning',
      title: this.translocoService.translate('home.notification.sessionTimeout.title'),
      message: this.translocoService.translate('home.notification.sessionTimeout.message', { seconds }),
      timestamp: new Date().toISOString(),
      read: false,
    };

    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

    // Only push to notification center (bell menu), the toast is handled by the template
    if (!currentCenter.some(n => n.id === IDLE_NOTIFICATION_ID)) {
      this.subscriptionService.$notificationsCenter.next([...currentCenter, notification]);
    }
  }

  private updateIdleNotificationMessage(seconds: number): void {
    const update = (list: any[]) =>
      list.map(n => n.id === IDLE_NOTIFICATION_ID
        ? { ...n, message: this.translocoService.translate('home.notification.sessionTimeout.message', { seconds }) }
        : n
      );

    this.subscriptionService.$notificationsCenter.next(update(this.subscriptionService.$notificationsCenter.getValue()));
  }

  private removeIdleNotification(): void {
    const filter = (list: any[]) => list.filter(n => n.id !== IDLE_NOTIFICATION_ID);
    this.subscriptionService.$notificationsCenter.next(filter(this.subscriptionService.$notificationsCenter.getValue()));
  }

  private performLogout(): void {
    this.clearAllTimers();
    this._warning$.next(null);
    this.removeIdleNotification();

    // End the tracking session gracefully
    this.sessionService.disconnect();

    // Trigger MSAL logout — passing account lets MSAL auto-extract the login_hint
    // claim from the ID token and send it as logout_hint to skip the account picker.
    // PREREQUISITE: The "login_hint" optional claim must be enabled in the app
    // registration (Token configuration → Add optional claim → ID → login_hint).
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.instance.logoutRedirect({
      account,
      postLogoutRedirectUri: '/login',
    });
  }

  private clearAllTimers(): void {
    if (this.warningTimer) { clearTimeout(this.warningTimer); this.warningTimer = null; }
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    this.clearCountdown();
  }

  private clearCountdown(): void {
    if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
  }

  ngOnDestroy(): void {
    this.configSub?.unsubscribe();
    this.stopListening();
  }
}
